import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SelectUI from '../../../components/ui/Select';

const STORAGE_KEY = 'kapital_search_filters';

// Collapsible section component
const FilterSection = ({ title, iconName, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-white/5 last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4 px-0 text-left focus:outline-none group touch-manipulation min-h-[48px]"
        aria-expanded={isOpen}
      >
        <div className="flex items-center space-x-3">
          <Icon name={iconName} size={18} className="text-brand-gold" />
          <span className="font-heading font-bold text-sm text-white tracking-wide">{title}</span>
        </div>
        <Icon
          name="ChevronDown"
          size={16}
          className={`text-white/40 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[800px] opacity-100 pb-4' : 'max-h-0 opacity-0'
        }`}
      >
        {children}
      </div>
    </div>
  );
};

// Touch-friendly commune picker
const CommunePicker = ({ communes, value, onChange }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const communeOptions = communes?.map((c) => ({
    value: c?.name?.toLowerCase(),
    label: c?.name,
    district: c?.district,
    avgPrice: c?.average_price
  })) || [];

  const filtered = search ?
    communeOptions?.filter((c) =>
      c?.label?.toLowerCase()?.includes(search?.toLowerCase()) ||
      c?.district?.toLowerCase()?.includes(search?.toLowerCase())
    ) :
    communeOptions;

  const selected = communeOptions?.find((c) => c?.value === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef?.current && !containerRef?.current?.contains(e?.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-11 w-full items-center justify-between rounded-xl bg-white/5 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold transition-colors border-none"
      >
        <span className={selected ? 'text-white font-medium' : 'text-white/50'}>
          {selected ? selected?.label : 'Toutes les communes'}
        </span>
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/40" />
      </button>

      {/* Selected commune info */}
      {selected && (
        <div className="mt-2 px-4 py-2.5 bg-white/5 rounded-xl flex items-center justify-between border-none">
          <div>
            <span className="text-xs font-bold text-white/50 uppercase tracking-widest block">{selected?.district}</span>
            {selected?.avgPrice > 0 && (
              <span className="text-xs font-medium text-brand-gold mt-0.5 block">
                ~{(selected?.avgPrice / 1000000)?.toFixed(1)}M XOF/m²
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => onChange('')}
            className="w-6 h-6 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <Icon name="X" size={12} />
          </button>
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 top-full left-0 right-0 mt-2 bg-[#1A1A1A] rounded-xl shadow-2xl overflow-hidden border border-white/10">
          {/* Search input */}
          <div className="p-3 border-b border-white/5">
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
                placeholder="Rechercher..."
                className="flex h-10 w-full rounded-lg bg-white/5 pl-10 pr-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-brand-gold border-none"
                autoFocus 
              />
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-60 overflow-y-auto overscroll-contain py-2">
            <button
              type="button"
              onClick={() => handleSelect('')}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                !value ? 'bg-brand-gold/10 text-brand-gold font-bold' : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              Toutes les communes
            </button>

            {filtered?.map((commune) => (
              <button
                key={commune?.value}
                type="button"
                onClick={() => handleSelect(commune?.value)}
                className={`w-full text-left px-4 py-2.5 transition-colors ${
                  value === commune?.value ? 'bg-brand-gold/10' : 'hover:bg-white/5'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`text-sm font-medium ${value === commune?.value ? 'text-brand-gold' : 'text-white/80'}`}>
                      {commune?.label}
                    </span>
                    {commune?.district && (
                      <span className="block text-[10px] uppercase tracking-widest text-white/40 mt-1">{commune?.district}</span>
                    )}
                  </div>
                  {value === commune?.value && (
                    <Icon name="Check" size={16} className="text-brand-gold" />
                  )}
                </div>
              </button>
            ))}

            {filtered?.length === 0 && (
              <div className="px-4 py-6 text-sm text-white/40 text-center font-light">
                Aucune commune trouvée
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Price range slider
const PriceRangeSlider = ({ minPrice, maxPrice, onMinChange, onMaxChange }) => {
  const MIN = 0;
  const MAX = 500000000;
  const STEP = 5000000;

  const minVal = parseInt(minPrice) || MIN;
  const maxVal = parseInt(maxPrice) || MAX;

  const minRef = useRef(null);
  const maxRef = useRef(null);
  const rangeRef = useRef(null);

  const getPercent = useCallback(
    (value) => Math.round((value - MIN) / (MAX - MIN) * 100),
    []
  );

  useEffect(() => {
    if (rangeRef?.current) {
      const minPercent = getPercent(minVal);
      const maxPercent = getPercent(maxVal);
      rangeRef.current.style.left = `${minPercent}%`;
      rangeRef.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [minVal, maxVal, getPercent]);

  const formatPrice = (val) => {
    if (val >= 1000000) return `${(val / 1000000)?.toFixed(0)}M`;
    if (val >= 1000) return `${(val / 1000)?.toFixed(0)}K`;
    return val?.toString();
  };

  const priceRangeError = minPrice && maxPrice && parseInt(minPrice) > parseInt(maxPrice)
    ? 'Le prix minimum ne peut pas dépasser le max'
    : null;

  const presets = [
    { label: '< 50M', min: 0, max: 50000000 },
    { label: '50-150M', min: 50000000, max: 150000000 },
    { label: '150-300M', min: 150000000, max: 300000000 },
    { label: '300M+', min: 300000000, max: MAX }
  ];

  return (
    <div className="space-y-6">
      {/* Preset quick-select buttons */}
      <div className="grid grid-cols-4 gap-2">
        {presets?.map((preset) => {
          const isActive = minVal === preset?.min && maxVal === preset?.max;
          return (
            <button
              key={preset?.label}
              type="button"
              onClick={() => {
                onMinChange(preset?.min === 0 ? '' : preset?.min?.toString());
                onMaxChange(preset?.max === MAX ? '' : preset?.max?.toString());
              }}
              className={`py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${
                isActive ? 'bg-brand-gold text-black' : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
              }`}
            >
              {preset?.label}
            </button>
          );
        })}
      </div>

      {/* Dual range slider */}
      <div className="relative pt-2 pb-4">
        <div className={`relative h-1.5 rounded-full ${priceRangeError ? 'bg-red-500/20' : 'bg-white/10'}`}>
          <div
            ref={rangeRef}
            className="absolute h-full bg-brand-gold rounded-full" 
          />
        </div>

        <input
          ref={minRef}
          type="range"
          min={MIN}
          max={MAX}
          step={STEP}
          value={minVal}
          onChange={(e) => {
            const val = Math.min(parseInt(e?.target?.value), maxVal - STEP);
            onMinChange(val === MIN ? '' : val?.toString());
          }}
          className="absolute inset-0 w-full h-1.5 appearance-none bg-transparent cursor-pointer touch-slider"
          style={{ zIndex: minVal > MAX - STEP ? 5 : 3 }} 
        />

        <input
          ref={maxRef}
          type="range"
          min={MIN}
          max={MAX}
          step={STEP}
          value={maxVal}
          onChange={(e) => {
            const val = Math.max(parseInt(e?.target?.value), minVal + STEP);
            onMaxChange(val === MAX ? '' : val?.toString());
          }}
          className="absolute inset-0 w-full h-1.5 appearance-none bg-transparent cursor-pointer touch-slider"
          style={{ zIndex: 4 }} 
        />
      </div>

      {/* Price labels & manual inputs */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/5 rounded-xl p-3 border-none">
          <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-1 block">Min (XOF)</label>
          <input
            type="number"
            value={minPrice || ''}
            onChange={(e) => onMinChange(e?.target?.value)}
            placeholder="0"
            min={0}
            step={STEP}
            className={`w-full bg-transparent text-sm font-bold text-white focus:outline-none border-none p-0 ${
              priceRangeError ? 'text-red-400' : ''
            }`}
          />
        </div>
        <div className="bg-white/5 rounded-xl p-3 border-none">
          <label className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-1 block">Max (XOF)</label>
          <input
            type="number"
            value={maxPrice || ''}
            onChange={(e) => onMaxChange(e?.target?.value)}
            placeholder="Illimité"
            min={0}
            step={STEP}
            className={`w-full bg-transparent text-sm font-bold text-white focus:outline-none border-none p-0 ${
              priceRangeError ? 'text-red-400' : ''
            }`}
          />
        </div>
      </div>

      {/* Inline price range error */}
      {priceRangeError && (
        <div className="flex items-center gap-2 text-xs font-bold text-red-400 bg-red-500/10 rounded-lg px-3 py-2">
          <Icon name="AlertCircle" size={14} />
          <span>{priceRangeError}</span>
        </div>
      )}
    </div>
  );
};

// Touch-friendly toggle checkbox (iOS style dark mode)
const TouchCheckbox = ({ checked, onChange, label }) => (
  <label className="flex items-center justify-between py-3 px-4 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors select-none group border-none">
    <span className="text-sm font-medium text-white/80 group-hover:text-white transition-colors">{label}</span>
    <div
      className={`relative w-12 h-7 rounded-full transition-colors duration-300 ${
        checked ? 'bg-brand-gold' : 'bg-white/10'
      }`}
      onClick={() => onChange(!checked)}
    >
      <div
        className={`absolute top-1 bottom-1 w-5 bg-white rounded-full shadow-md transition-transform duration-300 ${
          checked ? 'translate-x-[22px]' : 'translate-x-1'
        }`} 
      />
    </div>
  </label>
);

// Bedroom/bathroom quick-select
const RoomSelector = ({ value, onChange, label }) => {
  const options = ['', '1', '2', '3', '4', '5+'];
  return (
    <div>
      <label className="block text-[10px] font-bold text-white/40 uppercase tracking-widest mb-2">{label}</label>
      <div className="flex gap-2">
        {options?.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt === value ? '' : opt)}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${
              value === opt 
                ? 'bg-brand-gold text-black' 
                : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
            }`}
          >
            {opt === '' ? 'Tous' : opt}
          </button>
        ))}
      </div>
    </div>
  );
};

// Load persisted filters from localStorage
const loadPersistedFilters = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {}
  return null;
};

const defaultFilters = {
  commune: '',
  propertyType: '',
  minPrice: '',
  maxPrice: '',
  bedrooms: '',
  bathrooms: '',
  proximity: '',
  securityLevel: '',
  hasGenerator: false,
  hasParking: false,
  furnished: false
};

const SearchFilters = ({
  filters,
  onFiltersChange,
  onClearFilters,
  isVisible,
  onToggle,
  communes = []
}) => {
  const [localFilters, setLocalFilters] = useState(() => {
    const persisted = loadPersistedFilters();
    return persisted || filters || defaultFilters;
  });

  useEffect(() => {
    const persisted = loadPersistedFilters();
    if (persisted) {
      onFiltersChange(persisted);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(localFilters));
    } catch (e) {}
  }, [localFilters]);

  const propertyTypeOptions = [
    { value: '', label: 'Tous les types' },
    { value: 'villa', label: 'Villa' },
    { value: 'appartement', label: 'Appartement' },
    { value: 'duplex', label: 'Duplex' },
    { value: 'studio', label: 'Studio' },
    { value: 'terrain', label: 'Terrain' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'bureau', label: 'Bureau' }
  ];

  const proximityOptions = [
    { value: '', label: 'Toutes les proximités' },
    { value: 'universite', label: 'Université FHB' },
    { value: 'plateau-business', label: 'Quartier Plateau' },
    { value: 'autoroute-nord', label: 'Autoroute du Nord' },
    { value: 'boulevard-lagunaire', label: 'Bvd Lagunaire' },
    { value: 'aeroport', label: 'Aéroport International' },
    { value: 'centre-commercial', label: 'Centre Commercial' },
    { value: 'hopital', label: 'Hôpital' },
    { value: 'ecole-internationale', label: 'École Internationale' }
  ];

  const securityLevelOptions = [
    { value: '', label: 'Tous niveaux' },
    { value: 'basic', label: 'Sécurité de base' },
    { value: 'standard', label: 'Sécurité standard' },
    { value: 'premium', label: 'Sécurité premium' },
    { value: 'luxury', label: 'Sécurité de luxe' }
  ];

  const handleFilterChange = (key, value) => {
    const updated = { ...localFilters, [key]: value };
    setLocalFilters(updated);
    onFiltersChange(updated);
  };

  const handleClear = () => {
    setLocalFilters(defaultFilters);
    try {localStorage.removeItem(STORAGE_KEY);} catch (e) {}
    onClearFilters();
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    if (window.innerWidth < 1024) {
      onToggle();
    }
  };

  const activeCount = [
    localFilters?.commune,
    localFilters?.propertyType,
    localFilters?.minPrice,
    localFilters?.maxPrice,
    localFilters?.bedrooms,
    localFilters?.bathrooms,
    localFilters?.proximity,
    localFilters?.securityLevel,
    localFilters?.hasGenerator,
    localFilters?.hasParking,
    localFilters?.furnished
  ]?.filter(Boolean)?.length;

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isVisible && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={onToggle} 
        />
      )}

      {/* Filter panel */}
      <div
        className={`
          fixed lg:static inset-y-0 left-0 z-50 lg:z-auto
          w-[85vw] max-w-sm lg:w-full lg:max-w-none
          bg-[#111111] lg:rounded-3xl lg:shadow-2xl border-none outline-none
          transform transition-transform duration-300 ease-in-out
          ${isVisible ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
          overflow-hidden
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-6 border-b border-white/5 bg-[#111111] sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-gold/10 flex items-center justify-center">
              <Icon name="SlidersHorizontal" size={20} className="text-brand-gold" />
            </div>
            <h3 className="font-heading font-bold text-xl text-white tracking-tight">Filtres</h3>
            {activeCount > 0 && (
              <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold bg-brand-gold text-black rounded-full shadow-lg shadow-brand-gold/20">
                {activeCount}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {activeCount > 0 && (
              <button
                type="button"
                onClick={handleClear}
                className="text-xs font-bold uppercase tracking-widest text-white/40 hover:text-red-400 transition-colors"
              >
                Effacer
              </button>
            )}
            <button
              type="button"
              onClick={onToggle}
              className="lg:hidden w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-white/10 transition-colors border-none"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        {/* Scrollable filter content */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-4 border-none scrollbar-hide">
          
          <FilterSection title="Localisation" iconName="MapPin" defaultOpen={true}>
            <CommunePicker
              communes={communes}
              value={localFilters?.commune}
              onChange={(val) => handleFilterChange('commune', val)} 
            />
          </FilterSection>

          <FilterSection title="Budget" iconName="DollarSign" defaultOpen={true}>
            <PriceRangeSlider
              minPrice={localFilters?.minPrice}
              maxPrice={localFilters?.maxPrice}
              onMinChange={(val) => handleFilterChange('minPrice', val)}
              onMaxChange={(val) => handleFilterChange('maxPrice', val)} 
            />
          </FilterSection>

          <FilterSection title="Type de bien" iconName="Home" defaultOpen={true}>
            {/* Note: Assurez-vous que le sous-composant SelectUI supporte un style dark si possible */}
            <SelectUI
              value={localFilters?.propertyType}
              onChange={(val) => handleFilterChange('propertyType', val || '')}
              options={propertyTypeOptions}
              placeholder="Tous les types"
              className="bg-white/5 border-none text-white rounded-xl"
            />
          </FilterSection>

          <FilterSection title="Agencement" iconName="Layout" defaultOpen={false}>
            <div className="space-y-5">
              <RoomSelector
                value={localFilters?.bedrooms}
                onChange={(val) => handleFilterChange('bedrooms', val)}
                label="Chambres" 
              />
              <RoomSelector
                value={localFilters?.bathrooms}
                onChange={(val) => handleFilterChange('bathrooms', val)}
                label="Salles d'eau" 
              />
            </div>
          </FilterSection>

          <FilterSection title="Environnement" iconName="Navigation" defaultOpen={false}>
            <SelectUI
              value={localFilters?.proximity}
              onChange={(val) => handleFilterChange('proximity', val || '')}
              options={proximityOptions}
              placeholder="Toutes les proximités"
              className="bg-white/5 border-none text-white rounded-xl"
            />
          </FilterSection>

          <FilterSection title="Prestations" iconName="Sparkles" defaultOpen={false}>
            <div className="space-y-4">
              <SelectUI
                value={localFilters?.securityLevel}
                onChange={(val) => handleFilterChange('securityLevel', val || '')}
                options={securityLevelOptions}
                placeholder="Niveau de sécurité"
                className="bg-white/5 border-none text-white rounded-xl"
              />
              <div className="space-y-3 pt-2">
                <TouchCheckbox
                  checked={localFilters?.hasGenerator}
                  onChange={(val) => handleFilterChange('hasGenerator', val)}
                  label="Groupe électrogène" 
                />
                <TouchCheckbox
                  checked={localFilters?.hasParking}
                  onChange={(val) => handleFilterChange('hasParking', val)}
                  label="Parking privé" 
                />
                <TouchCheckbox
                  checked={localFilters?.furnished}
                  onChange={(val) => handleFilterChange('furnished', val)}
                  label="Bien meublé" 
                />
              </div>
            </div>
          </FilterSection>
        </div>

        {/* Mobile sticky footer */}
        <div className="lg:hidden p-6 border-t border-white/5 bg-[#111111]">
          <button
            type="button"
            onClick={handleApplyFilters}
            className="w-full py-4 bg-brand-gold text-black font-bold rounded-2xl hover:bg-yellow-400 transition-colors flex items-center justify-center gap-3 shadow-lg border-none"
          >
            <Icon name="Search" size={18} />
            Afficher les résultats
            {activeCount > 0 && (
              <span className="ml-2 bg-black/20 text-black text-xs px-2 py-0.5 rounded-full font-bold">
                {activeCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Slider thumb styles overrides for dark mode */}
      <style>{`
        .touch-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #C9A84C;
          border: 3px solid #111111;
          box-shadow: 0 0 10px rgba(201, 168, 76, 0.5);
          cursor: pointer;
          transition: transform 0.1s;
        }
        .touch-slider::-webkit-slider-thumb:active {
          transform: scale(1.15);
        }
        .touch-slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #C9A84C;
          border: 3px solid #111111;
          box-shadow: 0 0 10px rgba(201, 168, 76, 0.5);
          cursor: pointer;
        }
        .touch-slider::-webkit-slider-runnable-track { background: transparent; }
        .touch-slider::-moz-range-track { background: transparent; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </>
  );
};

export default SearchFilters;